/**
 * Turn a breakdown into a self-contained HTML page and either hand it to the
 * browser's print dialog (where the user picks "Save as PDF") or host it in a
 * hidden frame for another consumer — the image export screenshots the same
 * page (see breakdownImage.js).
 *
 * The document is rendered to a self-contained HTML string — its own <style>,
 * none of the app's — and loaded into a hidden same-origin <iframe srcdoc>.
 * Compared with the previous popup approach this cannot be popup-blocked, needs
 * no stylesheet sweep, and never has to delete `@page` rules out of Bootstrap at
 * runtime.
 */
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";

import BreakdownPrintDocument from "./BreakdownPrintDocument";
import { PRINT_CSS } from "./printStyles";

// The same family the app already loads, so the document matches the brand;
// the stylesheet's fallback stack covers the offline case.
const POPPINS_CSS_URL = "https://fonts.googleapis.com/css?family=Poppins:400,500,600&display=swap";

// How long a frame may live if `afterprint` never fires (some browsers skip it
// when the dialog is cancelled). Long, because the print preview is modal and
// removing the frame underneath it would blank the preview.
const FRAME_LIFETIME_MS = 10 * 60 * 1000;

export function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

export function breakdownDocumentTitle(patient) {
  const name = [patient && patient.first_name, patient && patient.last_name].filter(Boolean).join(" ").trim();
  return name ? `Insurance Breakdown - ${name}` : "Insurance Breakdown";
}

/**
 * The complete HTML document for the frame. `mode` is "print" (default) or
 * "image": the image mode stamps `data-export="image"` on <html> so the
 * stylesheet can lay the page out for a screen capture instead of paper.
 */
export function buildBreakdownPrintHtml(props, { mode = "print" } = {}) {
  const body = renderToStaticMarkup(<BreakdownPrintDocument {...props} />);
  const htmlAttrs = mode === "image" ? ' data-export="image"' : "";
  return (
    `<!DOCTYPE html><html${htmlAttrs}><head>` +
    '<meta charset="utf-8"/>' +
    `<title>${escapeHtml(breakdownDocumentTitle(props.patient))}</title>` +
    `<link rel="stylesheet" href="${POPPINS_CSS_URL}"/>` +
    `<style>${PRINT_CSS}</style>` +
    "</head><body>" + body + "</body></html>"
  );
}

/**
 * Load `html` into a hidden same-origin frame. Resolves once the frame has
 * loaded and its web font has landed, with `{ win, doc, iframe, cleanup }`.
 * `width` (CSS px) sizes the frame's viewport — the print path leaves it at 0
 * (nothing is displayed), the image path needs the page's real width so the
 * layout matches paper.
 */
export function mountHiddenFrame(html, { width = 0, height = 0 } = {}) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "Breakdown document");
    // Kept off-screen rather than display:none — a frame that is not rendered
    // has no layout, and both print and html2canvas need one.
    iframe.style.cssText =
      `position:fixed;left:-100000px;top:0;width:${width}px;height:${height}px;border:0;` +
      "visibility:hidden;pointer-events:none;";

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      const doc = win && win.document;
      if (!win || !doc) {
        cleanup();
        reject(new Error("Document frame unavailable"));
        return;
      }
      const done = () => resolve({ win, doc, iframe, cleanup });
      // Let the webfont land before layout is used; otherwise print preview can
      // reflow mid-render and a capture can catch the fallback font.
      const fonts = doc.fonts;
      if (fonts && fonts.ready && typeof fonts.ready.then === "function") {
        fonts.ready.then(() => setTimeout(done, 50)).catch(done);
      } else {
        setTimeout(done, 250);
      }
    };

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

/**
 * Load `html` into a hidden frame and open the print dialog for it. Resolves
 * once the dialog has been requested; rejects if the frame cannot be created.
 * The frame is removed after printing (or on a safety timeout).
 */
export async function printHtmlDocument({ html }) {
  const { win, cleanup } = await mountHiddenFrame(html);
  let timer = null;
  const finish = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    cleanup();
  };
  try {
    win.addEventListener("afterprint", finish);
    timer = setTimeout(finish, FRAME_LIFETIME_MS);
    win.focus();
    win.print();
  } catch (err) {
    finish();
    throw err;
  }
}
